package com.example.mystybag.appCore.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.appCore.logic.PersonalDetailsViewModel
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.model.IconData
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.CircleIconView
import com.example.mystybag.common.ui.components.custom.ImageDescriptionView
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p14
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p60
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.logic.DualTextInputBottomSheetChild
import com.example.mystybag.common.logic.TextInputBottomSheetChild
import com.example.mystybag.appCore.logic.CitySelectionBottomSheetChild
import com.example.mystybag.appCore.logic.PhoneSelectionBottomSheetChild
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.common.views.CommonBottomSheetFactory
import com.example.mystybag.common.views.DualTextInputBottomSheetView
import com.example.mystybag.common.views.TextInputBottomSheetView
import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import androidx.compose.ui.text.style.TextAlign

@Composable
fun PersonalDetailsView(viewModel: PersonalDetailsViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    if (rememberIsWebDesktopLayout()) {
        PersonalDetailsWebView(viewModel)
        return
    }

    BottomSheetUIView(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .scrollEdgeFade(),
        viewModel = viewModel,
        showTopBar = true,
        navigationContent = {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(p48)
                        .clip(CircleShape)
                        .background(AppTheme.current.colors.surfaceContainerLowest)
                        .noRippleClickable {
                            keyboardController?.hide()
                            viewModel.navCallbacks.backButtonCallback()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.arrow_back),
                        contentDescription = null,
                        contentScale = ContentScale.Fit,
                        modifier = Modifier.size(width = 10.dp, height = 18.dp),
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
                    )
                }
                Texts.TitleMedium(
                    text = sharedString(SR.strings.profile),
                    modifier = Modifier.weight(1f),
                    textAlign = TextAlign.Center,
                    color = AppTheme.current.colors.onSurface
                )
                Spacer(modifier = Modifier.size(p48))
            }
        },
        bottomSheetFactory = {
            when (it) {
                is TextInputBottomSheetChild -> TextInputBottomSheetView(it.viewModel)
                is DualTextInputBottomSheetChild -> DualTextInputBottomSheetView(it.viewModel)
                is CitySelectionBottomSheetChild -> CitySelectionBottomSheetView(it.viewModel)
                is PhoneSelectionBottomSheetChild -> PhoneSelectionBottomSheetView(it.viewModel)
                else -> CommonBottomSheetFactory(it)
            }
        }
    ) { uiState ->
        PersonalDetailsContent(
            uiState = uiState,
            viewModel = viewModel,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = p16)
        )
    }
}

@Composable
internal fun PersonalDetailsContent(
    uiState: androidx.compose.runtime.State<com.example.mystybag.appCore.model.PersonalDetailsUIState>,
    viewModel: PersonalDetailsViewModel,
    modifier: Modifier
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(p32)
    ) {
        // Profile picture: 124dp circle + 48dp camera overlay (Figma)
        Box(
            modifier = Modifier
                .size(LocalTheme.p124)
                .align(Alignment.CenterHorizontally)
                .noRippleClickable(viewModel::onAvatarImageButtonClicked),
            contentAlignment = Alignment.BottomEnd
        ) {
            ImageDescriptionView(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(CircleShape),
                image = uiState.value.userImage
            )
            CircleIconView(
                modifier = Modifier.border(p2, AppTheme.current.colors.surfaceDim, CircleShape),
                model = IconData(
                    circleSize = p48,
                    iconSize = p24,
                    icon = painterResource(SRC.images.camera_outlined_icon)
                )
            )
        }

        PersonalDetailsSection(
            sectionTitle = stringResource(SR.strings.section_personal_info),
            items = listOf(
                Triple(stringResource(SR.strings.email), uiState.value.email, viewModel::onEmailRowClick),
                Triple(stringResource(SR.strings.mobile_number), listOfNotNull(uiState.value.dialCode, uiState.value.phoneNumber).filter { it.isNotEmpty() }.joinToString(" "), viewModel::onPhoneRowClick),
                Triple(stringResource(SR.strings.name), uiState.value.userName, viewModel::onNameRowClick),
                Triple(stringResource(SR.strings.city), uiState.value.city, viewModel::onCityRowClick)
            )
        )

        PersonalDetailsSection(
            sectionTitle = stringResource(SR.strings.section_other),
            items = listOf(
                Triple(stringResource(SR.strings.delete_account), "", viewModel::onDeleteAccountClick)
            )
        )
    }
}

@Composable
private fun PersonalDetailsSection(
    sectionTitle: String,
    items: List<Triple<String, String, () -> Unit>>
) {
    // Същата структура като MenuView / ProfilePageView: section header + itemSpacing 8 между редовете
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p8)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            Texts.LabelMedium(
                text = sectionTitle.uppercase(),
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(LocalTheme.p16)
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p2)
                    .background(AppTheme.current.colors.onSurfaceVariant.copy(alpha = 0.4f))
            )
        }
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p8)
        ) {
            items.forEach { (label, value, onClick) ->
                PersonalDetailsRow(
                    label = label,
                    value = value,
                    onClick = onClick
                )
            }
        }
    }
}

@Composable
private fun PersonalDetailsRow(
    label: String,
    value: String,
    onClick: () -> Unit
) {
    // Същият ред като MenuView AccountListItem: 60dp, padding 8, roundPadding 8, лейбълът подравнен вляво
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(p60)
            .rippleClick(padding = p8, roundPadding = p8, action = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.CenterStart),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Texts.TitleMedium(
                text = label,
                color = AppTheme.current.colors.onSurface,
                modifier = Modifier.weight(1f)
            )
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p8)
            ) {
                if (value.isNotEmpty()) {
                    Texts.BodyMedium(
                        text = value,
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                }
                Image(
                    painter = painterResource(SRC.images.right_arrow),
                    contentDescription = null,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.size(width = p8, height = p14),
                    colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurfaceVariant)
                )
            }
        }
    }
}
